import type { USState } from "./us-state-code";
import { US_STATES } from "./us-state-code";

/** US state codes that support E-Verify */
export const E_VERIFY_STATE_CODE_VALUES = [
  "AK",
  "AL",
  "AR",
  "AS",
  "AZ",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "GU",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MP",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
] as const;

export type EVerifyStateCode = (typeof E_VERIFY_STATE_CODE_VALUES)[number];

/** US states that support E-Verify (with name and abbreviation) */
export const E_VERIFY_STATES: readonly USState[] = US_STATES.filter((state) =>
  (E_VERIFY_STATE_CODE_VALUES as readonly string[]).includes(state.abbreviation)
);

/** E-Verify country codes for citizenship/nationality verification */
export const E_VERIFY_COUNTRY_CODES = [
  { name: "Afghanistan", abbreviation: "AFG" },
  { name: "Angola", abbreviation: "AGO" },
  { name: "Albania", abbreviation: "ALB" },
  { name: "Andorra", abbreviation: "AND" },
  { name: "United Arab Emirates", abbreviation: "ARE" },
  { name: "Argentina", abbreviation: "ARG" },
  { name: "Armenia", abbreviation: "ARM" },
  { name: "Antigua and Barbuda", abbreviation: "ATG" },
  { name: "Australia", abbreviation: "AUS" },
  { name: "Austria", abbreviation: "AUT" },
  { name: "Azerbaijan", abbreviation: "AZE" },
  { name: "Burundi", abbreviation: "BDI" },
  { name: "Belgium", abbreviation: "BEL" },
  { name: "Benin", abbreviation: "BEN" },
  { name: "Burkina Faso", abbreviation: "BFA" },
  { name: "Bangladesh", abbreviation: "BGD" },
  { name: "Bulgaria", abbreviation: "BGR" },
  { name: "Bahrain", abbreviation: "BHR" },
  { name: "Bahamas", abbreviation: "BHS" },
  { name: "Bosnia and Herzegovina", abbreviation: "BIH" },
  { name: "Belarus", abbreviation: "BLR" },
  { name: "Belize", abbreviation: "BLZ" },
  { name: "Bolivia", abbreviation: "BOL" },
  { name: "Brazil", abbreviation: "BRA" },
  { name: "Barbados", abbreviation: "BRB" },
  { name: "Brunei", abbreviation: "BRN" },
  { name: "Bhutan", abbreviation: "BTN" },
  { name: "Botswana", abbreviation: "BWA" },
  { name: "Central African Republic", abbreviation: "CAF" },
  { name: "Canada", abbreviation: "CAN" },
  { name: "Switzerland", abbreviation: "CHE" },
  { name: "Chile", abbreviation: "CHL" },
  { name: "China", abbreviation: "CHN" },
  { name: "Côte d'Ivoire", abbreviation: "CIV" },
  { name: "Cameroon", abbreviation: "CMR" },
  { name: "Democratic Republic of the Congo", abbreviation: "COD" },
  { name: "Republic of the Congo", abbreviation: "COG" },
  { name: "Colombia", abbreviation: "COL" },
  { name: "Comoros", abbreviation: "COM" },
  { name: "Cape Verde", abbreviation: "CPV" },
  { name: "Costa Rica", abbreviation: "CRI" },
  { name: "Cuba", abbreviation: "CUB" },
  { name: "Cyprus", abbreviation: "CYP" },
  { name: "Czech Republic", abbreviation: "CZE" },
  { name: "Germany", abbreviation: "DEU" },
  { name: "Djibouti", abbreviation: "DJI" },
  { name: "Dominica", abbreviation: "DMA" },
  { name: "Denmark", abbreviation: "DNK" },
  { name: "Dominican Republic", abbreviation: "DOM" },
  { name: "Algeria", abbreviation: "DZA" },
  { name: "Ecuador", abbreviation: "ECU" },
  { name: "Egypt", abbreviation: "EGY" },
  { name: "Eritrea", abbreviation: "ERI" },
  { name: "Spain", abbreviation: "ESP" },
  { name: "Estonia", abbreviation: "EST" },
  { name: "Ethiopia", abbreviation: "ETH" },
  { name: "Finland", abbreviation: "FIN" },
  { name: "Fiji", abbreviation: "FJI" },
  { name: "France", abbreviation: "FRA" },
  { name: "Micronesia", abbreviation: "FSM" },
  { name: "Gabon", abbreviation: "GAB" },
  { name: "United Kingdom", abbreviation: "GBR" },
  { name: "Georgia", abbreviation: "GEO" },
  { name: "Ghana", abbreviation: "GHA" },
  { name: "Guinea", abbreviation: "GIN" },
  { name: "Gambia", abbreviation: "GMB" },
  { name: "Guinea-Bissau", abbreviation: "GNB" },
  { name: "Equatorial Guinea", abbreviation: "GNQ" },
  { name: "Greece", abbreviation: "GRC" },
  { name: "Grenada", abbreviation: "GRD" },
  { name: "Guatemala", abbreviation: "GTM" },
  { name: "Guyana", abbreviation: "GUY" },
  { name: "Honduras", abbreviation: "HND" },
  { name: "Croatia", abbreviation: "HRV" },
  { name: "Haiti", abbreviation: "HTI" },
  { name: "Hungary", abbreviation: "HUN" },
  { name: "Indonesia", abbreviation: "IDN" },
  { name: "India", abbreviation: "IND" },
  { name: "Ireland", abbreviation: "IRL" },
  { name: "Iran", abbreviation: "IRN" },
  { name: "Iraq", abbreviation: "IRQ" },
  { name: "Iceland", abbreviation: "ISL" },
  { name: "Israel", abbreviation: "ISR" },
  { name: "Italy", abbreviation: "ITA" },
  { name: "Jamaica", abbreviation: "JAM" },
  { name: "Jordan", abbreviation: "JOR" },
  { name: "Japan", abbreviation: "JPN" },
  { name: "Kazakhstan", abbreviation: "KAZ" },
  { name: "Kenya", abbreviation: "KEN" },
  { name: "Kyrgyzstan", abbreviation: "KGZ" },
  { name: "Cambodia", abbreviation: "KHM" },
  { name: "Kiribati", abbreviation: "KIR" },
  { name: "Saint Kitts and Nevis", abbreviation: "KNA" },
  { name: "South Korea", abbreviation: "KOR" },
  { name: "Kuwait", abbreviation: "KWT" },
  { name: "Laos", abbreviation: "LAO" },
  { name: "Lebanon", abbreviation: "LBN" },
  { name: "Liberia", abbreviation: "LBR" },
  { name: "Libya", abbreviation: "LBY" },
  { name: "Saint Lucia", abbreviation: "LCA" },
  { name: "Liechtenstein", abbreviation: "LIE" },
  { name: "Sri Lanka", abbreviation: "LKA" },
  { name: "Lesotho", abbreviation: "LSO" },
  { name: "Lithuania", abbreviation: "LTU" },
  { name: "Luxembourg", abbreviation: "LUX" },
  { name: "Latvia", abbreviation: "LVA" },
  { name: "Morocco", abbreviation: "MAR" },
  { name: "Monaco", abbreviation: "MCO" },
  { name: "Moldova", abbreviation: "MDA" },
  { name: "Madagascar", abbreviation: "MDG" },
  { name: "Maldives", abbreviation: "MDV" },
  { name: "Mexico", abbreviation: "MEX" },
  { name: "Marshall Islands", abbreviation: "MHL" },
  { name: "North Macedonia", abbreviation: "MKD" },
  { name: "Mali", abbreviation: "MLI" },
  { name: "Malta", abbreviation: "MLT" },
  { name: "Myanmar", abbreviation: "MMR" },
  { name: "Montenegro", abbreviation: "MNE" },
  { name: "Mongolia", abbreviation: "MNG" },
  { name: "Mozambique", abbreviation: "MOZ" },
  { name: "Mauritania", abbreviation: "MRT" },
  { name: "Mauritius", abbreviation: "MUS" },
  { name: "Malawi", abbreviation: "MWI" },
  { name: "Malaysia", abbreviation: "MYS" },
  { name: "Namibia", abbreviation: "NAM" },
  { name: "Niger", abbreviation: "NER" },
  { name: "Nigeria", abbreviation: "NGA" },
  { name: "Nicaragua", abbreviation: "NIC" },
  { name: "Netherlands", abbreviation: "NLD" },
  { name: "Norway", abbreviation: "NOR" },
  { name: "Nepal", abbreviation: "NPL" },
  { name: "Nauru", abbreviation: "NRU" },
  { name: "New Zealand", abbreviation: "NZL" },
  { name: "Oman", abbreviation: "OMN" },
  { name: "Pakistan", abbreviation: "PAK" },
  { name: "Panama", abbreviation: "PAN" },
  { name: "Peru", abbreviation: "PER" },
  { name: "Philippines", abbreviation: "PHL" },
  { name: "Palau", abbreviation: "PLW" },
  { name: "Papua New Guinea", abbreviation: "PNG" },
  { name: "Poland", abbreviation: "POL" },
  { name: "North Korea", abbreviation: "PRK" },
  { name: "Portugal", abbreviation: "PRT" },
  { name: "Paraguay", abbreviation: "PRY" },
  { name: "Palestine", abbreviation: "PSE" },
  { name: "Qatar", abbreviation: "QAT" },
  { name: "Romania", abbreviation: "ROU" },
  { name: "Russia", abbreviation: "RUS" },
  { name: "Rwanda", abbreviation: "RWA" },
  { name: "Saudi Arabia", abbreviation: "SAU" },
  { name: "Sudan", abbreviation: "SDN" },
  { name: "Senegal", abbreviation: "SEN" },
  { name: "Singapore", abbreviation: "SGP" },
  { name: "Solomon Islands", abbreviation: "SLB" },
  { name: "Sierra Leone", abbreviation: "SLE" },
  { name: "El Salvador", abbreviation: "SLV" },
  { name: "San Marino", abbreviation: "SMR" },
  { name: "Somalia", abbreviation: "SOM" },
  { name: "Serbia", abbreviation: "SRB" },
  { name: "South Sudan", abbreviation: "SSD" },
  { name: "São Tomé and Príncipe", abbreviation: "STP" },
  { name: "Suriname", abbreviation: "SUR" },
  { name: "Slovakia", abbreviation: "SVK" },
  { name: "Slovenia", abbreviation: "SVN" },
  { name: "Sweden", abbreviation: "SWE" },
  { name: "Eswatini", abbreviation: "SWZ" },
  { name: "Seychelles", abbreviation: "SYC" },
  { name: "Syria", abbreviation: "SYR" },
  { name: "Chad", abbreviation: "TCD" },
  { name: "Togo", abbreviation: "TGO" },
  { name: "Thailand", abbreviation: "THA" },
  { name: "Tajikistan", abbreviation: "TJK" },
  { name: "Turkmenistan", abbreviation: "TKM" },
  { name: "Timor-Leste", abbreviation: "TLS" },
  { name: "Tonga", abbreviation: "TON" },
  { name: "Trinidad and Tobago", abbreviation: "TTO" },
  { name: "Tunisia", abbreviation: "TUN" },
  { name: "Turkey", abbreviation: "TUR" },
  { name: "Tuvalu", abbreviation: "TUV" },
  { name: "Taiwan", abbreviation: "TWN" },
  { name: "Tanzania", abbreviation: "TZA" },
  { name: "Uganda", abbreviation: "UGA" },
  { name: "Ukraine", abbreviation: "UKR" },
  { name: "Uruguay", abbreviation: "URY" },
  { name: "Uzbekistan", abbreviation: "UZB" },
  { name: "Vatican City", abbreviation: "VAT" },
  { name: "Saint Vincent and the Grenadines", abbreviation: "VCT" },
  { name: "Venezuela", abbreviation: "VEN" },
  { name: "Vietnam", abbreviation: "VNM" },
  { name: "Vanuatu", abbreviation: "VUT" },
  { name: "Samoa", abbreviation: "WSM" },
  { name: "Kosovo", abbreviation: "XKS" },
  { name: "Yemen", abbreviation: "YEM" },
  { name: "South Africa", abbreviation: "ZAF" },
  { name: "Zambia", abbreviation: "ZMB" },
  { name: "Zimbabwe", abbreviation: "ZWE" },
] as const;

export type EVerifyCountryCode = (typeof E_VERIFY_COUNTRY_CODES)[number]["abbreviation"];

/** E-Verify case status values */
export const EVERIFY_CASE_STATUS_VALUES = [
  "DRAFT",
  "UNCONFIRMED_DATA",
  "PHOTO_MATCH",
  "SCAN_AND_UPLOAD",
  "MANUAL_REVIEW",
  "PENDING_REFERRAL",
  "REFERRED",
  "QUEUED",
  "EMPLOYMENT_AUTHORIZED",
  "FINAL_NONCONFIRMATION",
  "CLOSE_CASE_AND_RESUBMIT",
  "CLOSED",
] as const;

export type EVerifyCaseStatus = (typeof EVERIFY_CASE_STATUS_VALUES)[number];

/** E-Verify eligibility statement values */
export const EVERIFY_ELIGIBILITY_STATEMENT_VALUES = [
  "EMPLOYMENT_AUTHORIZED",
  "NOT_AUTHORIZED_EMPLOYMENT",
  "RESUBMIT_CASE",
  "NO_SHOW",
  "NO_ACTION_FNC",
] as const;

export type EVerifyEligibilityStatement = (typeof EVERIFY_ELIGIBILITY_STATEMENT_VALUES)[number];

/** Citizenship status codes used in E-Verify case submissions */
export const CITIZENSHIP_STATUS_CODE_VALUES = [
  "US_CITIZEN",
  "NONCITIZEN",
  "LAWFUL_PERMANENT_RESIDENT",
  "ALIEN_AUTHORIZED_TO_WORK",
  "NONCITIZEN_AUTHORIZED_TO_WORK",
] as const;

export type CitizenshipStatusCode = (typeof CITIZENSHIP_STATUS_CODE_VALUES)[number];

/** Reason codes for continuing with a duplicate E-Verify case */
export const DUPLICATE_CONTINUE_REASON_CODE_VALUES = [
  "REHIRE",
  "INCORRECT_INFORMATION_ENTERED",
  "DHS_ASKED_ME_TO_CREATE_A_NEW_CASE",
  "SSA_ASKED_ME_TO_CREATE_A_NEW_CASE",
  "PREVIOUS_CASE_RESULT_RESUBMIT",
  "OTHER",
] as const;

export type DuplicateContinueReasonCode = (typeof DUPLICATE_CONTINUE_REASON_CODE_VALUES)[number];

/** Case closure reason codes returned by E-Verify */
export const CASE_CLOSURE_REASON_CODE_VALUES = [
  "INVALID_QUERY",
  "EMPLOYMENT_NOT_AUTHORIZED",
  "SELF_TERMINATED",
  "EMPLOYEE_NOT_TERMINATED",
  "FORCED_CLOSURE",
  "REVIEWED",
  "OPERATOR_ERROR",
  "EMPLOYMENT_AUTHORIZED",
  "DUPLICATE_CASE_ENTERED_BY_EMPLOYER",
  "EXPIRED_DOCUMENT",
  "EMPLOYER_INSTRUCTED_TO_CREATE_NEW_CASE",
  "TECHNICAL_ISSUES_WITH_EVERIFY",
  "EMPLOYEE_CONTINUES_TO_WORK_AFTER_EA",
  "EMPLOYEE_CONTINUES_TO_WORK_AFTER_FNC",
  "EMPLOYEE_CONTINUES_TO_WORK_AFTER_NO_SHOW",
  "EMPLOYEE_CONTINUES_TO_WORK_AFTER_UNCONTESTED_TNC",
  "EMPLOYEE_TERMINATED_FOR_FNC_RESULT",
  "EMPLOYEE_QUIT",
  "EMPLOYEE_TERMINATED_FOR_OTHER_REASONS",
  "EMPLOYEE_TERMINATED_FOR_NO_SHOW",
  "EMPLOYEE_TERMINATED_FOR_UNCONTESTED_TNC",
  "DUPLICATE_CASE_DATA_EXISTS",
  "INCORRECT_DATA",
  "EMPLOYER_INSTRUCTED_TO_CLOSE_BY_DHS",
  "EMPLOYER_INSTRUCTED_TO_CLOSE_BY_SSA",
  "OTHER",
  "OTHER_FREE_TEXT",
  "ADMINISTRATIVELY_CLOSED",
  "ADMINISTRATIVELY_CLOSED_TERMINATED_ACCT",
  "INCOMPLETE_ADMIN_CLOSED",
  "EA_AUTO_CLOSE",
  "EMPLOYER_IS_NO_LONGER_CLIENT",
  "EMPLOYER_INSTRUCTED_TO_CLOSE",
  "NO_LONGER_EMPLOYED",
] as const;

export type CaseClosureReasonCode = (typeof CASE_CLOSURE_REASON_CODE_VALUES)[number];

/** Reason for delay codes for E-Verify case submissions */
export const REASON_FOR_DELAY_CODE_VALUES = [
  "AWAITING_SOCIAL_SECURITY_NUMBER",
  "TECHNICAL_PROBLEMS",
  "AUDIT_REVEALED_THAT_NEW_HIRE_WAS_NOT_RUN",
  "AWAITING_RESPONSE_ON_CASE_WITH_INCORRECT_INFO",
  "FEDERAL_CONTRACTOR",
  "OTHER",
] as const;

export type ReasonForDelayCode = (typeof REASON_FOR_DELAY_CODE_VALUES)[number];

/** Field names that can be confirmed/updated in an E-Verify case */
export const EVERIFY_CASE_FIELD_VALUES = [
  "first_name",
  "middle_initial",
  "last_name",
  "other_last_names_used",
  "date_of_birth",
  "ssn",
  "alien_number",
  "citizenship_status_code",
  "document_a_type_code",
  "document_b_type_code",
  "document_c_type_code",
  "document_sub_type_code",
  "i94_number",
  "i551_number",
  "i766_number",
  "us_passport_number",
  "foreign_passport_number",
  "document_bc_number",
  "expiration_date",
  "country_code",
  "us_state_code",
  "visa_number",
  "sevis_number",
  "no_expiration_date",
  "date_of_hire",
  "phone_number",
  "employee_email_address",
] as const;

export type EVerifyCaseField = (typeof EVERIFY_CASE_FIELD_VALUES)[number];

/** Photo match code values for E-Verify photo verification */
export const PHOTO_MATCH_CODE_VALUES = ["matching", "not-matching", "no-image"] as const;

export type PhotoMatchCode = (typeof PHOTO_MATCH_CODE_VALUES)[number];

/** Further Action Notice language values */
export const FAN_LANGUAGE_VALUES = ["english", "spanish"] as const;

export type FANLanguage = (typeof FAN_LANGUAGE_VALUES)[number];

/** E-Verify case action values */
export const EVERIFY_CASE_ACTION_VALUES = [
  "edit_case",
  "submit_case",
  "close_case",
  "confirm_details_fields",
  "confirm_details",
  "retrieve_photo",
  "photo_match",
  "scan_and_upload",
  "download_further_action_notice",
  "refer_case",
  "no_action",
  "download_referral_date_confirmation",
] as const;

export type EVerifyCaseActionValue = (typeof EVERIFY_CASE_ACTION_VALUES)[number];

/** E-Verify case filter values */
export const CASE_FILTER_VALUES = [
  "open_cases",
  "cases_with_new_updates",
  "cases_in_progress",
  "work_docs_expiring",
  "cases_to_be_closed",
  "closed_cases",
  "recently_auto_closed",
  "recently_auto_closed_count",
  "draft_cases",
  "fnc_cases",
  "ea_cases",
  "ready_for_review",
  "pending_employee_response",
  "tnc_cases",
] as const;

export type CaseFilter = (typeof CASE_FILTER_VALUES)[number];

/** E-Verify alert type values */
export const ALERT_TYPE_VALUES = [
  "CASES_TO_BE_CLOSED",
  "WORK_DOCS_EXPIRING",
  "CASES_WITH_NEW_UPDATES",
] as const;

export type AlertType = (typeof ALERT_TYPE_VALUES)[number];

/** Represents a duplicate continue reason with code and name */
export type DuplicateContinueReason = {
  code: DuplicateContinueReasonCode;
  name: string;
};

/** Represents a citizenship status with code and name */
export type CitizenshipStatus = {
  code: CitizenshipStatusCode;
  name: string;
};

/** Represents a reason for delay with code and name */
export type ReasonForDelay = {
  code: ReasonForDelayCode;
  name: string;
};

/** Represents a case closure reason with code and name */
export type CaseClosureReason = {
  code: CaseClosureReasonCode;
  name: string;
};

/**
 * Type for a field to be confirmed/updated in an E-Verify case.
 * Uses conditional types to enforce:
 * - value is boolean when field_name is "no_expiration_date"
 * - value is string for all other field names
 */
export type ConfirmDetailsField = {
  field_name: EVerifyCaseField;
  value: EVerifyCaseField extends "no_expiration_date" ? boolean : string;
};

export const CASE_SORT_FIELD_VALUES = ["last_name", "case_status", "case_number", "hire_date"] as const;
export type CaseSortField = (typeof CASE_SORT_FIELD_VALUES)[number];

export const SORT_DIRECTION_VALUES = ["ASC", "DESC"] as const;
export type SortDirection = (typeof SORT_DIRECTION_VALUES)[number];
